import React, { useEffect, useRef, useState } from "react";
import styles from "./ChatbotTemplates.module.scss";
import { Helmet } from "react-helmet";
import {
  ChatBotDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { message, Modal, Popconfirm, Spin } from "antd";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import { LanguageService } from "../../services/languages.service";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { FaPlus } from "react-icons/fa";
import { ChatBotTemplatesService } from "../../services/chatbot-template.service";
const ChatbotTemplates = () => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<ChatBotDataType[]>([]);
  const [childCategories, setChildCategories] = useState<
    Record<string, ChatBotDataType[]>
  >({});
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(
    null
  );
  const [parentCategoryId, setParentCategoryId] = useState<number | null>(null);
  const [childCategoriesLoading, setChildCategoriesLoading] = useState<
    Record<string, boolean>
  >({});
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const initialCategoryValues = useRef({
    title: "",
    link_title: "",
    title_link: "",
    translations: [] as {
      lang_code: string;
      title: string;
      link_title: string;
    }[],
  });

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getChildCategory = (parentId: number) => {
    if (childCategories[parentId]) return;
    setChildCategoriesLoading((prev) => ({
      ...prev,
      [parentId]: true,
    }));
    ChatBotTemplatesService.getChatBotTemplateChild(parentId)
      .then((response) => {
        if (response?.data?.data) {
          setChildCategories((prev) => ({
            ...prev,
            [parentId]: response?.data.data,
          }));
        }
      })
      .finally(() => {
        setChildCategoriesLoading((prev) => ({
          ...prev,
          [parentId]: false,
        }));
      });
  };

  useEffect(() => {
    setLoading(true);
    ChatBotTemplatesService.getAllChatBotTemplates()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          setCategories(response?.data?.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const {
    handleSubmit,
    values,
    resetForm,
    touched,
    errors,
    setFieldValue,
    handleChange,
  } = useFormik({
    initialValues: {
      title: "",
      link_title: "",
      title_link: "",
      translations: [] as {
        lang_code: string;
        title: string;
        link_title: string;
      }[],
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Chatbot Şablon adı tələb olunur"),
    }),
    onSubmit: async (values) => {
      try {
        const azTranslation = {
          lang_code: "AZ",
          title: values.title,
          title_link: values.link_title,
        };
        const updatedTranslations = [
          azTranslation,
          ...values.translations.filter((t) => t.lang_code !== "AZ"),
        ];
        const finalValues = {
          ...values,
          translations: updatedTranslations,
        };
        if (isEditing) {
          const isEqual =
            JSON.stringify(values.title) ===
              JSON.stringify(initialCategoryValues.current.title) &&
            JSON.stringify(values.link_title) ===
              JSON.stringify(initialCategoryValues.current.link_title) &&
            JSON.stringify(values.title_link) ===
              JSON.stringify(initialCategoryValues.current.title_link) &&
            JSON.stringify(values.translations) ===
              JSON.stringify(initialCategoryValues.current.translations);
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            const response =
              await ChatBotTemplatesService.updateChatBotTemplate({
                ...finalValues,
                id: editingCategoryId,
              });
            if (response.status === 200) {
              message.success("Chatbot Şablon uğurla yeniləndi!");
              setLoading(true);
              setIsModalVisible(false);
              resetForm();
              const updatedData =
                await ChatBotTemplatesService.getAllChatBotTemplates();
              setCategories(
                updatedData?.data?.data?.map(
                  (item: ChatBotDataType, index: number) => ({
                    ...item,
                    key: index + 1,
                  })
                )
              );

              setChildCategories((prevChildCategories: any) => {
                const updatedChildCategories = { ...prevChildCategories };
                const parentId = response?.data?.data?.parent_id;
                if (updatedChildCategories[parentId]) {
                  updatedChildCategories[parentId] = updatedChildCategories[
                    parentId
                  ].map((child: any) => {
                    if (child.id === response?.data?.data?.id) {
                      return {
                        ...child,
                        title: response?.data?.data?.title,
                        link_title: response?.data?.data?.link_title,
                      };
                    }
                    return child;
                  });
                }
                return updatedChildCategories;
              });
            } else {
              message.error(
                response.errorMessage || "Chatbot Şablonu yeniləmək alınmadı."
              );
            }
          }
        } else {
          const newCategory = {
            ...finalValues,
            parent_id: parentCategoryId || null,
          };
          const response = await ChatBotTemplatesService.addChatBotTemplate({
            ...finalValues,
            parent_id: parentCategoryId,
          });
          if (response.status === 201) {
            message.success("Chatbot Şablon uğurla əlavə edildi!");
            setLoading(true);
            setIsModalVisible(false);
            resetForm();
            if (parentCategoryId) {
              setChildCategories((prevChildCategories: any) => ({
                ...prevChildCategories,
                [parentCategoryId]: [
                  ...(prevChildCategories[parentCategoryId] || []),
                  { ...newCategory, id: response?.data?.data?.id },
                ],
              }));
              setChildCategories((prevChildCategories) => {
                const updatedChildCategories = { ...prevChildCategories };
                Object.keys(updatedChildCategories).forEach((key) => {
                  updatedChildCategories[key] = updatedChildCategories[key].map(
                    (category) =>
                      category.id === parentCategoryId
                        ? { ...category, is_parent: true }
                        : category
                  );
                });
                return updatedChildCategories;
              });
              setCategories((prevCategories) =>
                prevCategories.map((category) =>
                  category?.id === parentCategoryId
                    ? { ...category, is_parent: true }
                    : category
                )
              );
              setExpandedRows((prev) => ({
                ...prev,
                [parentCategoryId]: true,
              }));
            } else {
              setCategories((prevCategories) => [
                ...prevCategories,
                {
                  parent_id: null,
                  title: values.title,
                } as ChatBotDataType,
              ]);
              const updatedData =
                await ChatBotTemplatesService.getAllChatBotTemplates();
              setCategories(
                updatedData?.data?.data?.map(
                  (item: ChatBotDataType, index: number) => ({
                    ...item,
                    key: index + 1,
                  })
                )
              );
            }
          } else {
            message.error(
              response.errorMessage || "Chatbot Şablon əlavə etmək alınmadı."
            );
          }
        }
      } catch (error: any) {
        message.error(error.message || "Əməliyyat uğursuz oldu.");
      } finally {
        setLoading(false);
      }
    },
  });
  const handleTranslationChange = (
    value: string,
    field: "title" | "link_title"
  ) => {
    if (langCode === "AZ") {
      setFieldValue(field, value);
    } else {
      console.log(values.translations);

      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          title: field === "title" ? value : "",
          link_title: field === "link_title" ? value : "",
        });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: ChatBotDataType) => {
    try {
      const response = await ChatBotTemplatesService.deleteChatBotTemplate(
        record.id
      );
      if (response.status === 200) {
        setCategories((prevData) =>
          prevData.filter((item) => item.id !== record.id)
        );
        setChildCategories((prevChildCategories) => {
          const updatedChildCategories = { ...prevChildCategories };
          if (updatedChildCategories[record.id]) {
            delete updatedChildCategories[record.id];
          }
          Object.keys(updatedChildCategories).forEach((key) => {
            updatedChildCategories[key] = updatedChildCategories[key].filter(
              (childCategory) => childCategory.id !== record.id
            );
          });
          return updatedChildCategories;
        });
        if (record.parent_id) {
          const response =
            await ChatBotTemplatesService.getChatBotTemplateChild(
              record.parent_id
            );
          const updatedChildCategories = response?.data?.data;

          setChildCategories((prevChildCategories) => ({
            ...prevChildCategories,
            [record.parent_id]: updatedChildCategories,
          }));
          const isParent = updatedChildCategories.length > 0;
          setCategories((prevCategories) =>
            prevCategories.map((category) =>
              category.id === record.parent_id
                ? { ...category, is_parent: isParent }
                : category
            )
          );
          setExpandedRows((prev) => ({
            ...prev,
            [record.parent_id]: isParent && prev[record.parent_id],
          }));
        }
        message.success("Chatbot Şablon uğurla silindi!");
      } else {
        message.error(response.errorMessage || "Silmek alinmadi");
      }
    } catch (error) {
      message.error("Chatbot Şablonu silmək alınmadı.");
    }
  };

  const handleAddClick = () => {
    setParentCategoryId(null);
    setIsModalVisible(true);
    resetForm();
    setIsEditing(false);
    setLangCode("AZ");
  };
  
  const handleChildAddClick = (parentId: number) => {
    setParentCategoryId(parentId);
    setIsEditing(false);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
  };

  const handleEditClick = (category: ChatBotDataType) => {
    setEditingCategoryId(category.id);
    setIsEditing(true);
    setIsModalVisible(true);
    setFieldValue("title", category.title);
    setFieldValue("title_link", category.title_link);
    setFieldValue("link_title", category.link_title);
    setFieldValue("translations", category.translatieons || []);
    setLangCode("AZ");
    initialCategoryValues.current = {
      title: category.title,
      link_title: category.link_title,
      title_link: category.title_link,
      translations: category.translatieons || [],
    };
  };

  const renderCategories = (parentId: any | null, depth: number = 0) => {
    const parentCategories =
      parentId === null
        ? categories.filter((category) => category.parent_id === null)
        : childCategories[parentId] || [];

    if (!parentCategories.length) return null;

    return (
      <table className={styles.nestedTable}>
        <thead>
          <tr>
            <th></th>
            <th>№</th>
            <th>Şablon adı</th>
            <th>Link başlığı</th>
            <th>
              <div className={styles.settings}>
                <i className="fa-solid fa-gear"></i>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {parentCategories.map((category, index) => {
            const categoryKey = `${category.id}`;
            const isExpanded = expandedRows[categoryKey];
            const isLoading = childCategoriesLoading[category?.id];

            const hasChildren = childCategories[category.id]?.length > 0;
            const showIcon =
              (category.parent_id === null ||
                category.is_parent ||
                hasChildren) &&
              !(
                childCategories[category.id] &&
                childCategories[category.id].length === 0
              );
            const alwaysShowIcon = category.parent_id === null;
            return (
              <React.Fragment key={index}>
                <tr
                  onMouseEnter={() => setHoveredRowId(category.id)}
                  onMouseLeave={() => setHoveredRowId(null)}
                >
                  <td>
                    {showIcon || alwaysShowIcon ? (
                      <div className={styles.expand_icons}>
                        <span
                          className={`${styles.toggleIcon} ${
                            isExpanded ? styles.open : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(categoryKey);
                            if (!isExpanded) {
                              getChildCategory(category.id);
                            }
                          }}
                        >
                          {" "}
                          {isExpanded ? "-" : "+"}{" "}
                        </span>
                      </div>
                    ) : (
                      <span></span>
                    )}{" "}
                  </td>

                  <td>{index + 1}</td>
                  <td>{category.title}</td>
                  <td>{category.link_title || "-"}</td>
                  <td>
                    <div
                      className={`${styles.actions_icons} ${
                        hoveredRowId === category.id ||
                        category.parent_id === null
                          ? styles.visible
                          : styles.unvisible
                      }`}
                    >
                      <div
                        className={styles.edit}
                        onClick={() => handleEditClick(category)}
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </div>
                      <Popconfirm
                        title="Bu chatbot şablonu siləcəyinizə əminsiniz?"
                        onConfirm={() => handleDeleteClick(category)}
                        onCancel={() =>
                          message.info("Silinmə əməliyyatı ləğv edildi.")
                        }
                        okText="Bəli"
                        cancelText="Xeyr"
                        placement="topRight"
                      >
                        <div className={styles.delete}>
                          <i className="fa-solid fa-trash"></i>
                        </div>
                      </Popconfirm>
                      <div
                        className={styles.add}
                        onClick={() => handleChildAddClick(category.id)}
                      >
                        <i className="fa-solid fa-square-plus"></i>
                      </div>
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={5}>
                      {isLoading ? (
                        <div
                          style={{ padding: "30px 0", marginLeft: "-120px" }}
                        >
                          <Spin size="large" />
                        </div>
                      ) : (
                        renderCategories(category.id, depth + 1)
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div id={styles.categories_page}>
      <Helmet>
        <title>1412 | Chatbot Şablonlar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <h1 className={styles.page_title}>Chatbot Şablonlar</h1>
          <div className={styles.categories}>
            <div className={styles.table_head}>
              <MainButton onClick={handleAddClick}>
                <FaPlus size={20} />
              </MainButton>
            </div>
            <div className={styles.categories_wrapper}>
              <div className={styles.category}>
                <table className={styles.mainTable}>
                  <tbody>{renderCategories(null)}</tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      <Modal
        title={
          isEditing
            ? "Chatbot Şablona düzəliş edin"
            : "Yeni Chatbot Şablon əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        centered
        style={{ minWidth: "340px" }}
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Şablon adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                name="title"
                id="title"
                placeholder="Şablon adı"
                value={
                  langCode === "AZ"
                    ? values.title
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.title || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title")
                }
              />
              <div className="focus_border"></div>
            </div>
            <ErrorMessage error={errors.title} touched={touched.title} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="name">
              Link başlığı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                name="link_title"
                id="link_title"
                placeholder="Link başlığı"
                value={
                  langCode === "AZ"
                    ? values.link_title
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.link_title || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "link_title")
                }
              />
              <div className="focus_border"></div>
            </div>
          </div>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Link url
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                name="title_link"
                id="title_link"
                placeholder="Link url"
                value={values.title_link}
                onChange={handleChange}
              />
              <div className="focus_border"></div>
            </div>
          </div>
          <MainButton full type="submit">
            {isEditing ? "Redaktə edin" : "Əlavə edin"}
          </MainButton>
        </form>
      </Modal>
    </div>
  );
};

export default ChatbotTemplates;
